import classNames from 'classnames';
import { ReactNode } from 'react';
import { InfoCircleIcon } from '../icons';
import { formatValidationError } from '../utils/validation-error.ts';
import styles from './Tabs.module.css';
import { TooltipNew } from './TooltipNew.tsx';

export interface SelectButtonOption<T> {
  label: ReactNode;
  value: T;
  isError?: boolean;
}

export interface SelectButtonsProps<T> {
  options: SelectButtonOption<T>[];
  selected?: SelectButtonOption<T>;
  onChange: (value: T) => void;
  label?: ReactNode;
  hintLabel?: string;
  className?: string;
  disabled?: boolean;
  capitalize?: boolean;
  error?: string;
}

export const SelectButtons = <T,>({
  options,
  selected,
  onChange,
  className,
  label,
  hintLabel,
  disabled = false,
  capitalize = true,
  error,
}: SelectButtonsProps<T>) => {
  const handleClickOption = (option: SelectButtonOption<T>) => {
    onChange(option.value);
  };
  const resolveSelected = selected; //|| options[0];
  return (
    <div
      className={classNames(
        {
          'w-full': !className?.includes('w-'),
        },
        className,
      )}
    >
      {label && (
        <label
          className={classNames(
            'flex flex-row items-center justify-between text-[10px] leading-[18px]',
            'font-[Inter] text-gray-900 text-opacity-[.64] uppercase font-medium',
          )}
        >
          <span>{label}</span>
          {hintLabel && (
            <TooltipNew text={hintLabel}>
              <img alt="info" src={InfoCircleIcon} className="h-4 w-4 min-h-4 min-w-4" />
            </TooltipNew>
          )}
        </label>
      )}

      <div
        className={classNames(
          'flex items-center bg-gray-100 rounded min-h-[36px] divide-x divide-gray-200',
          {
            'cursor-not-allowed opacity-70': disabled,
          },
        )}
      >
        {options.map(({ label, value, isError }, index) => (
          <div key={index} className="w-full flex items-center justify-center">
            <button
              type="button"
              onClick={() => handleClickOption({ label, value })}
              disabled={disabled}
              className={classNames(
                'mx-[2px] w-full min-h-[32px] rounded-sm',
                'font-[Inter] text-[14px] leading-[20px] transition-all duration-300',
                {
                  'bg-white border-[1px] border-blue-700': resolveSelected?.value === value,
                  'text-gray-600 hover:text-gray-900': resolveSelected?.value !== value,
                  'cursor-not-allowed text-gray-600': disabled,
                  capitalize: capitalize,
                  'border-red-500 border-[1px]': isError && resolveSelected?.value !== value,
                  [styles.jumpAnimation]: isError && resolveSelected?.value !== value,
                },
              )}
            >
              {label}
            </button>
          </div>
        ))}
      </div>
      {error && (
        <p className="mt-1 text-sm text-[12px] text-error-red">
          {formatValidationError(error, 'choose one of the available options')}
        </p>
      )}
    </div>
  );
};
